import { useCallback, useMemo, useState } from "react";
import { truncateText } from "../../../../utils";
import Address from "./Address";
import { theme } from "../../../../theme";
import Label from "../../common/Label";
import EditAddress from "../address/EditAddress";
import YourPostcodeDetails from "../priceCheckDetails/YourPostcodeDetails";
import HeaderTexts from "../../common/HeaderTexts";
import AddressSearch from "../address/AddressSearch";

const fieldsToShow = [
    { key: "street_name_and_number", icon: "check_circle", color: "success" },
    { key: "neighborhood", icon: "holiday_village", color: "danger" },
    { key: "postal_town", icon: "location_on", color: "danger" },
    { key: "postcode", icon: "location_on", color: "danger" },
    { key: "address", icon: "location_on", color: "danger" },
    { key: "location", icon: "map", color: "dark" },
];

const FooterButtons = ({ name, color, icon, action }) => (
    <button
        type="button"
        aria-label="Cancel service"
        className={`btn btn-icon bg-gradient-${color} mt-3 mb-0 w-50`}
        onClick={action}
    >
        <span className="btn-inner--icon">
            <i className="material-symbols-rounded">{icon}</i>
        </span>
        &nbsp;
        <span className="btn-inner--text">{name}</span>
    </button>
);

const DashboardAddressButtons = ({ name, icon, action }) => (
    <div className="d-flex me-3 cursor-pointer" onClick={action}>
        <i className="material-symbols-rounded text-sm me-1">{icon}</i>
        <span className="text-sm me-2">{name}</span>
    </div>
);

const EnterYourPostcode = ({
    address,
    setAddress,
    jobGeolocationDetails,
    setJobGeolocationDetails,
    handleReset,
    showAddressSearch,
    isHome = false,
    retryJob,
    stepFlowIsEnable,
    formEditAddressRef = null,
    onStepSuccess = null,
    isCheckingPrice
}) => {
    const [openEditAddress, setOpenEditAddress] = useState(false);

    const handleOpenEditAddress = useCallback(() => setOpenEditAddress(true), []);
    const handleCloseEditAddress = useCallback(() => setOpenEditAddress(false), []);

    const placeName = useMemo(
        () => truncateText(address?.value?.Place || "N/A", 15),
        [address]
    );

    const addressButtons = [
        { name: "Reset", color: theme.close_btn, icon: "refresh", action: handleReset },
        { name: "Edit", color: theme.main, icon: "edit", action: handleOpenEditAddress },
    ];

    // ✅ After valid save, update state then advance step via onStepSuccess
    const handleUpdateAddress = useCallback((newAddressData) => {
        const updatedAddress = Object.values(newAddressData).filter(Boolean).join(", ");

        setJobGeolocationDetails({
            ...newAddressData,
            address: updatedAddress,
            location: jobGeolocationDetails?.location || null,
        });

        handleCloseEditAddress();

        if (onStepSuccess) {
            onStepSuccess(); // ✅ advance step only on valid submit
        }
    }, [jobGeolocationDetails, setJobGeolocationDetails, handleCloseEditAddress, onStepSuccess]);

    if (isHome) {
        return (
            <>
                {stepFlowIsEnable ? (
                    <div className="mx-3">
                        <HeaderTexts
                            title="Where should the work be done?"
                        />
                        <AddressSearch
                            address={address}
                            setAddress={setAddress}
                            jobGeolocationDetails={jobGeolocationDetails}
                            setJobGeolocationDetails={setJobGeolocationDetails}
                            handleUpdateAddress={handleUpdateAddress}
                            formEditAddressRef={formEditAddressRef}
                            isHome={isHome}
                            isCheckingPrice={isCheckingPrice}
                        />
                    </div>
                ) : (
                    <>
                        {!showAddressSearch ? (
                            <div className="d-flex">
                                <div className="flex-grow-1 ms-3">
                                    <Label text="Address" />
                                    <p className="text-ms font-bold">{jobGeolocationDetails?.address}</p>
                                    {retryJob === 0 && (
                                        <div className="d-flex">
                                            {addressButtons.map((btn, index) => (
                                                <DashboardAddressButtons
                                                    key={index}
                                                    name={btn.name}
                                                    icon={btn.icon}
                                                    action={btn.action}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <Address
                                address={address}
                                setAddress={setAddress}
                                handleReset={handleReset}
                                retryJob={retryJob}
                            />
                        )}

                        <EditAddress
                            show={openEditAddress}
                            closeModal={handleCloseEditAddress}
                            jobGeolocationDetails={jobGeolocationDetails}
                            onUpdateAddress={handleUpdateAddress}
                            showModalView={true}
                        />
                    </>
                )}
            </>
        );
    }

    return (
        <div className="card h-100 border border-dark">
            <span className={`badge rounded-pill bg-${theme.main} text-light text-bolder w-50 mt-n3 mx-auto`}>
                Your postcode
            </span>

            <div className="card-header text-center pt-4 pb-3">
                <h3 className="font-weight-bold mt-2">{placeName}</h3>
            </div>

            <div className="card-body text-lg-start text-center pt-0">
                {showAddressSearch && (
                    <Address address={address} setAddress={setAddress} handleReset={handleReset} retryJob={retryJob}/>
                )}

                <YourPostcodeDetails isHome={isHome} />

                {jobGeolocationDetails && (
                    <div className="mt-4">
                        {fieldsToShow.map((field, index) => {
                            const value = jobGeolocationDetails[field.key];
                            if (!value) return null;
                            return (
                                <div key={index} className="d-flex justify-content-start p-2">
                                    <i className={`material-symbols-rounded my-auto text-${theme.main} text-${field.color}`}>
                                        {field.icon}
                                    </i>
                                    {field.key !== "location" ? (
                                        <span className="ps-3">{value}</span>
                                    ) : (
                                        <a
                                            className="ps-3 text-secondary font-bold"
                                            href={`https://www.google.com/maps?q=${value?.lat},${value?.lng}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="Open location in Google Maps"
                                        >
                                            Open location
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {!showAddressSearch && (
                <div className="card-footer text-center pt-0">
                    <div className="d-flex gap-2">
                        {addressButtons.map((btn, index) => (
                            <FooterButtons
                                key={index}
                                name={btn.name}
                                color={btn.color}
                                icon={btn.icon}
                                action={btn.action}
                            />
                        ))}
                    </div>
                </div>
            )}

            <EditAddress
                show={openEditAddress}
                closeModal={handleCloseEditAddress}
                jobGeolocationDetails={jobGeolocationDetails}
                onUpdateAddress={handleUpdateAddress}
            />
        </div>
    );
};

export default EnterYourPostcode;